import { ChartColumn, Expand } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { cn } from "@/shared/lib/cn";
import { motionTokens } from "@/shared/lib/motion";

type AgePyramidPoint = {
  label: string;
  value: number;
};

type EnterpriseDashboardAgePyramidProps = {
  title: string;
  subtitle: string;
  viewAllLabel?: string;
  detailHref?: string;
  data: AgePyramidPoint[];
};

/** Une couleur par tranche d'âge, de la plus jeune à la plus âgée (reprise en boucle au-delà). */
const BAR_COLORS = [
  { fill: "bg-[linear-gradient(90deg,#a6f21b,#6fae14)]", dot: "bg-[#8bd016]" },
  { fill: "bg-[linear-gradient(90deg,#6ee7b7,#10b981)]", dot: "bg-emerald-500" },
  { fill: "bg-[linear-gradient(90deg,#93c5fd,#3b82f6)]", dot: "bg-blue-500" },
  { fill: "bg-[linear-gradient(90deg,#c4b5fd,#8b5cf6)]", dot: "bg-violet-500" },
  { fill: "bg-[linear-gradient(90deg,#fdba74,#f97316)]", dot: "bg-orange-500" },
  { fill: "bg-[linear-gradient(90deg,#f9a8d4,#ec4899)]", dot: "bg-pink-500" },
] as const;

const formatPercent = (value: number) =>
  `${value.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`;

export function EnterpriseDashboardAgePyramid({
  title,
  subtitle,
  viewAllLabel,
  detailHref,
  data,
}: EnterpriseDashboardAgePyramidProps) {
  const card = (
    <div
      className={`theme-card flex h-full flex-col p-5${detailHref ? " cursor-pointer transition-shadow hover:shadow-md" : ""}`}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-soft text-primary">
            <ChartColumn size={20} strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            <p className="mt-1 text-xs text-muted">{subtitle}</p>
          </div>
        </div>
        {viewAllLabel && detailHref ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
            {viewAllLabel}
            <Expand size={13} />
          </span>
        ) : null}
      </div>

      {/* Barres de progression : part de l'effectif par tranche d'âge (piste = 100 %). */}
      <ul className="flex min-h-[260px] flex-1 flex-col justify-center gap-4">
        {data.map((point, index) => {
          const color = BAR_COLORS[index % BAR_COLORS.length];
          const width = Math.min(100, Math.max(0, point.value));

          return (
            <li key={point.label}>
              <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
                <span className="flex items-center gap-2 text-foreground">
                  <span className={cn("h-2 w-2 shrink-0 rounded-full", color.dot)} aria-hidden="true" />
                  {point.label}
                </span>
                <span className="font-semibold tabular-nums text-foreground">{formatPercent(point.value)}</span>
              </div>
              <div
                className="h-2.5 overflow-hidden rounded-full bg-card-muted"
                role="progressbar"
                aria-label={point.label}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={point.value}
              >
                <motion.div
                  className={cn("h-full origin-left rounded-full", color.fill)}
                  style={{ width: `${width}%` }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{
                    duration: motionTokens.duration.slow,
                    ease: motionTokens.easing.smooth,
                    delay: 0.1 + index * motionTokens.stagger,
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );

  if (detailHref) {
    return (
      <Link to={detailHref} className="block h-full no-underline">
        {card}
      </Link>
    );
  }

  return card;
}
